"use client";
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, keyframes } from "@mui/material";

const fillAnimation = keyframes`
  0% { clip-path: inset(0 100% 0 0); }
  50%, 100% { clip-path: inset(0 0 0 0); }
`;

const logoAnimation = keyframes`
  from {
    top: 50%;
    transform: translate(-50%, -50%);
  }
  to {
    top: 50px;
    transform: translate(-50%, 0);
  }
`;

const backgroundFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; pointer-events: none; }
`;

const GradientLogoLoading: React.FC<{ isLoading: boolean }> = ({
	isLoading,
}) => {
	const [animationStarted, setAnimationStarted] = useState(false);
	const [animationFinished, setAnimationFinished] = useState(false);
	const animationHasRun = useRef(false);

	useEffect(() => {
		if (!isLoading) {
			if (!animationHasRun.current) {
				// First time: run animation
				setAnimationStarted(true);
				animationHasRun.current = true;
				const timer = setTimeout(() => {
					setAnimationFinished(true);
				}, 1000); // Animation duration
				return () => clearTimeout(timer);
			} else {
				// Subsequent times: just set final state, no animation
				setAnimationStarted(true);
				setAnimationFinished(true);
			}
		}
	}, [isLoading]);

	const commonTextStyles = {
        // Используем шрифт из темы (Inter)
		// fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
		fontSize: "40px",
		fontWeight: "bold",
		lineHeight: "60px",
	};

	const logoContent = (
		<>
			{isLoading && (
				<Typography
					variant="h4"
					component="h1"
					sx={{
						...commonTextStyles,
						color: "#78716c",
						width: "100%",
						textAlign: "center",
					}}
				>
					AVADA VPN
				</Typography>
			)}
			<Typography
				variant="h4"
				component="h1"
				sx={{
					...commonTextStyles,
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					textAlign: "center",
					background:
						"linear-gradient(170deg, #fff7ed 40%, #ea580c 60%)",
					WebkitBackgroundClip: "text",
					WebkitTextFillColor: "transparent",
					backgroundClip: "text",
					animation: isLoading
						? `${fillAnimation} 2.5s cubic-bezier(0.25, 0.1, 0.25, 1.0) infinite`
						: "none",
				}}
			>
				AVADA VPN
			</Typography>
		</>
	);

	return (
		<>
			{!animationFinished && (
				<Box
					sx={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "var(--tg-theme-bg-color)",
						zIndex: 9998,
						animation: animationStarted
							? `${backgroundFadeOut} 1s forwards`
							: "none",
					}}
				/>
			)}
			<Box
				sx={{
					position: "fixed",
					left: "50%",
					zIndex: animationFinished ? 1 : 9998,
					width: "280px",
					height: "60px",
					top: "50%",
					transform: "translate(-50%, -50%)",
					animation: animationStarted
						? `${logoAnimation} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`
						: "none",
				}}
			>
				{logoContent}
			</Box>
		</>
	);
};

export default GradientLogoLoading;
